import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@ai-tutor/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useShellSlots } from '@/contexts/ShellSlotsContext';
import { useUserProfile } from '@/hooks/account/useUser';
import {
  BookOpen,
  Archive,
  BarChart3,
  Settings,
  Menu,
  X,
  Home,
  Sprout,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { UserProfile } from '../common/UserProfile';
import { BrandBar } from './BrandBar';

export function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  // The running focus timer, filled in by app/provider.tsx (see ShellSlots).
  const { navbarAccessory } = useShellSlots();
  const location = useLocation();

  const userName =
    profile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Student';
  // Avatar key is deterministic per user, so the URL string never changes
  // across re-uploads - append updated_at to bust the browser cache.
  const avatarUrl = profile?.avatar_url
    ? `${profile.avatar_url}?v=${encodeURIComponent(profile.updated_at)}`
    : null;

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/my-learning', label: 'My Learning', icon: BookOpen },
    { path: '/mindspace', label: 'Mindspace', icon: Archive },
    { path: '/progress', label: 'Progress', icon: BarChart3 },
    { path: '/wellbeing', label: 'Wellbeing', icon: Sprout },
  ];

  // The sheet covers the viewport, so the page behind it must not scroll under
  // it, and Escape has to work: the old dropdown put its Escape handler on the
  // click-scrim, which never held focus, so the key did nothing in practice.
  // A document listener is the only version that actually closes the menu.
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* The bar shell and the brand lockup live in `BrandBar`, which auth and
          onboarding also render. Navbar is that plus the things only a
          signed-in app has: sections, profile, and the mobile sheet. */}
      <BrandBar linkHome>
        {/* Desktop Navigation - Center */}
        {/* Five sections: the gap steps down below `lg` so they still fit
            beside the brand and the profile at tablet width. */}
        <div className='hidden items-center space-x-1 md:flex lg:space-x-6'>
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex transform items-center space-x-2 rounded-md px-3 py-2 text-sm font-medium transition-all duration-300 ease-out ${
                  isActive(item.path)
                    ? // DARK: the active pill used to be the SAME lavender
                      // as the Learn button, so navigation shouted as
                      // loudly as the primary action and the accent meant
                      // nothing. Quiet fill + bright label keeps it clearly
                      // active while leaving the CTA somewhere to stand
                      // out. LIGHT keeps the solid pill - a white-alpha
                      // fill on a white page is invisible.
                      `scale-105 bg-primary text-primary-foreground shadow-md dark:bg-white/[0.07] dark:text-foreground dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.09]`
                    : `hover:scale-102 bg-white/0 text-muted-foreground hover:bg-accent/80 hover:text-accent-foreground`
                }`}
                style={{
                  backfaceVisibility: 'hidden',
                  willChange: 'transform, background-color, color',
                }}
              >
                <Icon className='h-4 w-4' />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Desktop Profile - Right */}
        <div className='hidden items-center gap-3 md:flex'>
          {navbarAccessory}
          <UserProfile userName={userName} avatarUrl={avatarUrl} />
        </div>

        {/* Mobile menu button, with the timer beside it while one runs */}
        <div className='flex items-center gap-2 md:hidden'>
          {navbarAccessory}
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            // `tap-target`: drawn 40x32, tapped as 44x44 - it is the only way
            // into the nav on a phone, and it sits at the screen edge.
            className='tap-target bg-card/50 text-foreground hover:bg-card/70 hover:text-foreground'
            // Icon-only, so it needs a name of its own — without one it
            // is an unlabelled button and, on mobile, the ONLY route to
            // every nav item and Settings. It is also the only pointer
            // route back OUT of the sheet, since that has no scrim.
            aria-label={
              isMobileMenuOpen
                ? 'Close navigation menu'
                : 'Open navigation menu'
            }
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className='h-5 w-5' />
            ) : (
              <Menu className='h-5 w-5' />
            )}
          </Button>
        </div>
      </BrandBar>

      {/* Mobile Navigation Menu — a full-screen sheet, not a dropdown.
          It fills everything below the bar (`top-16 bottom-0`) and sits at
          z-40, under the z-50 navbar, so the brand and the toggle stay visible
          and the same button that opened it closes it.

          Solid `bg-card` in both themes rather than the navbar's glass: this
          covers the whole viewport, so anything showing through is page
          content directly behind the labels rather than the lattice. That also
          means no scrim — there is no visible page left to click on, which is
          why Escape (below) is now the keyboard route out instead of a
          focusable overlay nobody could reach. */}
      {isMobileMenuOpen && (
        <div className='fixed inset-x-0 bottom-0 top-16 z-40 flex flex-col bg-card motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 md:hidden'>
          {/* Only the links scroll; the account row stays pinned. */}
          <nav aria-label='Sections' className='flex-1 overflow-y-auto p-4'>
            <ul className='space-y-1'>
              {navItems.map((item, i) => (
                <li
                  key={item.path}
                  className='motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:fill-mode-both'
                  style={{ animationDelay: `${40 + i * 30}ms` }}
                >
                  <MobileNavItem
                    {...item}
                    active={isActive(item.path)}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                </li>
              ))}
            </ul>
          </nav>

          {/* Who is signed in, and the way to their settings - together, at
              the thumb's end of the screen. The name used to be a bare line
              of text here with a generic person icon, and Settings a sixth
              row among the sections. */}
          <div className='flex items-center gap-3 border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'>
            <AccountAvatar userName={userName} avatarUrl={avatarUrl} />
            <div className='min-w-0 flex-1'>
              <p className='truncate text-sm font-semibold text-foreground'>
                {userName}
              </p>
              <p className='text-xs text-muted-foreground'>Signed in</p>
            </div>
            <Link
              to='/settings'
              onClick={() => setIsMobileMenuOpen(false)}
              aria-current={isActive('/settings') ? 'page' : undefined}
              className='flex h-11 items-center gap-2 rounded-xl border border-border px-3.5 text-sm font-medium text-foreground transition-colors duration-fast hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            >
              <Settings
                className='h-4 w-4 text-muted-foreground'
                aria-hidden='true'
              />
              Settings
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

interface MobileNavItemProps {
  path: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  onNavigate: () => void;
}

/**
 * One section in the phone menu: an icon chip and the label. The active one
 * is marked quietly - a soft fill and a filled chip - rather than the solid
 * black (lavender in dark) slab it used to be, which out-shouted every
 * primary button in the app. The same call the desktop bar made for dark
 * mode, made here for both.
 */
function MobileNavItem({
  path,
  label,
  icon: Icon,
  active,
  onNavigate,
}: MobileNavItemProps) {
  return (
    <Link
      to={path}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`flex min-h-14 items-center gap-3.5 rounded-2xl px-3 text-base transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active
          ? 'bg-muted font-semibold text-foreground'
          : 'font-medium text-foreground hover:bg-muted/60'
      }`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-fast ${
          active
            ? 'bg-primary text-primary-foreground'
            : 'dark:tint-brand bg-brand-100 text-brand-700'
        }`}
      >
        <Icon className='h-5 w-5' aria-hidden='true' />
      </span>
      <span className='flex-1'>{label}</span>
      {active && (
        <span
          className='h-1.5 w-1.5 rounded-full bg-primary'
          aria-hidden='true'
        />
      )}
    </Link>
  );
}

/** The signed-in user's picture, or initials - the desktop bar's disc. */
function AccountAvatar({
  userName,
  avatarUrl,
}: {
  userName: string;
  avatarUrl: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const initials = userName
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <span
      aria-hidden='true'
      className='flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-400 text-sm font-semibold text-brand-950'
    >
      {avatarUrl && !failed ? (
        <img
          src={avatarUrl}
          alt=''
          className='h-full w-full object-cover'
          onError={() => setFailed(true)}
        />
      ) : (
        initials
      )}
    </span>
  );
}
